import { Component, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  FinanceService,
  MigrationJob,
  MigrationReportRowError,
  MigrationReportRowWarning,
} from '../../finance.service';
import { ToasterService } from '@app/shared/services/toaster.service';

/**
 * Admin-only modal for the one-time / on-demand D&T IND Resource File upload.
 *
 * <p>Flow:
 * <ol>
 *   <li>POST the workbook to /finance/admin/migration-upload — returns
 *       immediately with a MigrationJob in QUEUED status. No timeout risk on
 *       a multi-minute import.</li>
 *   <li>Poll GET /finance/admin/migration-status every {@link #POLL_INTERVAL_MS}
 *       ms, surfacing live counters (processed, imported, skipped, errors).</li>
 *   <li>When the job reaches COMPLETED, render the full MigrationReport;
 *       on FAILED, surface the BE-provided errorMessage.</li>
 * </ol>
 *
 * <p>Re-runs are safe: the BE idempotency key on (employee_id, project_id,
 * billing_start_date, capex_opex) means previously-imported rows show up in
 * the "Skipped" column rather than being re-inserted.
 */
@Component({
  selector: 'app-import-data-modal',
  templateUrl: './import-data-modal.component.html',
  styleUrl: './import-data-modal.component.scss',
})
export class ImportDataModalComponent implements OnDestroy {
  private static readonly POLL_INTERVAL_MS = 1500;

  selectedFile: File | null = null;
  /** True from the moment we POST until the job lands in COMPLETED or FAILED. */
  uploading = false;
  job: MigrationJob | null = null;
  errorMessage: string | null = null;

  readonly listLimit = 25;
  private pollSub?: Subscription;

  constructor(
    private readonly dialogRef: MatDialogRef<ImportDataModalComponent, MigrationJob | null>,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  get errorList(): MigrationReportRowError[] {
    return this.job?.report?.rowErrors ?? [];
  }

  get warningList(): MigrationReportRowWarning[] {
    return this.job?.report?.rowWarnings ?? [];
  }

  /**
   * "Done" / final state — the BE has reported COMPLETED or FAILED. Drives the
   * footer button swap (Upload → Done) and stops the polling spinner.
   */
  get isTerminal(): boolean {
    return this.job?.status === 'COMPLETED' || this.job?.status === 'FAILED';
  }

  /** 0-100 percent — calculated client-side from processed/totalRowsRead. */
  get progressPercent(): number {
    if (!this.job || !this.job.totalRowsRead) {
      return 0;
    }
    return Math.min(100, Math.round((this.job.processed / this.job.totalRowsRead) * 100));
  }

  onFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.selectedFile = null;
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.toaster.showError('Please choose a .xlsx file', 'Invalid file');
      this.selectedFile = null;
      input.value = '';
      return;
    }
    this.selectedFile = file;
    this.job = null;
    this.errorMessage = null;
  }

  clearSelection(): void {
    this.selectedFile = null;
    this.job = null;
    this.errorMessage = null;
    this.pollSub?.unsubscribe();
  }

  upload(): void {
    if (!this.selectedFile || this.uploading) {
      return;
    }
    this.uploading = true;
    this.errorMessage = null;
    this.financeService.startMigration(this.selectedFile).subscribe({
      next: (job) => {
        this.job = job;
        this.startPolling(job.jobId);
      },
      error: (err) => {
        this.uploading = false;
        console.error('Migration start failed', err);
        this.errorMessage = err?.error?.message
          ?? 'Failed to start the migration job. Please verify the file and try again.';
        this.toaster.showError(this.errorMessage ?? 'Upload failed', 'Error');
      },
    });
  }

  private startPolling(jobId: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = timer(0, ImportDataModalComponent.POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.financeService.getMigrationStatus(jobId)))
      .subscribe({
        next: (snapshot) => {
          this.job = snapshot;
          if (snapshot.status === 'COMPLETED') {
            this.uploading = false;
            this.pollSub?.unsubscribe();
            const report = snapshot.report;
            if (report && report.errors === 0) {
              this.toaster.showSuccess(
                `Imported ${report.imported} rows (skipped ${report.skippedIdempotent})`,
                'Upload complete',
              );
            } else if (report) {
              this.toaster.showError(
                `Imported ${report.imported}, ${report.errors} errors`,
                'Completed with errors',
              );
            }
          } else if (snapshot.status === 'FAILED') {
            this.uploading = false;
            this.pollSub?.unsubscribe();
            this.errorMessage = snapshot.errorMessage
              ?? 'Migration failed. See backend logs for details.';
            this.toaster.showError(this.errorMessage ?? 'Upload failed', 'Error');
          }
        },
        error: (err) => {
          // Polling errors are transient — log + keep polling. The user can
          // close the modal manually if it stays stuck.
          console.warn('migration-status poll failed (will retry)', err);
        },
      });
  }

  done(): void {
    this.dialogRef.close(this.job);
  }

  cancel(): void {
    // Modal close does NOT abort the BE job — the migration keeps running.
    // The admin can re-open the page later to see the result via the regular
    // Resource Info refresh.
    this.pollSub?.unsubscribe();
    this.dialogRef.close(null);
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
}
